// SelfScoreHeatmap — compact inline distribution bar for the federal
// 0-3 self-scoring rubric. Designed to live inside the Go/No-Go Quick
// Metrics card.
//
// v3.11.5 — Empty States UX Refactor:
//   Instead of returning null when there are no extracted requirements,
//   renders a compact collapsed "empty-state" row with a CTA to run the
//   AI Shredder on Opportunity Intake. Federal-template tools stay
//   discoverable on fresh pursuits without adding clutter.

import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { ExternalLink, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { cn } from '../../lib/utils';

const SEGMENT_COLORS = {
  red:    'bg-red-500',
  yellow: 'bg-amber-400',
  green:  'bg-emerald-500',
  blue:   'bg-blue-500',
  unscored: 'bg-slate-200',
};

const SEGMENT_LABELS = {
  red:    'Red — No Capability / No PP',
  yellow: 'Yellow — Some Understanding',
  green:  'Green — Good Understanding w/ 1 PP',
  blue:   'Blue — Expert Understanding w/ 2+ PPs',
  unscored: 'Un-scored',
};

// Empty-state — shown when no requirements have been extracted yet.
function EmptyState({ onNavigate, dataTestId }) {
  const [open, setOpen] = React.useState(false);
  const isClickable = typeof onNavigate === 'function';
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          data-testid={`${dataTestId}-empty`}
          className="w-full flex items-center gap-2 text-left rounded-md px-2 py-1.5 hover:bg-slate-100 transition-colors group"
        >
          {open
            ? <ChevronDown className="w-3 h-3 text-slate-400" />
            : <ChevronRight className="w-3 h-3 text-slate-400" />}
          <span className="text-sm text-slate-600 flex-1">Self-Score Coverage</span>
          <span className="text-[11px] text-slate-400 italic">No requirements yet</span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1 ml-5 pl-2 border-l border-slate-200 text-[11px] text-slate-500 space-y-1 py-1">
          <p className="flex items-start gap-1.5">
            <Info className="w-3 h-3 text-cyan-500 mt-0.5 shrink-0" />
            <span>
              The <strong className="text-slate-700">0–3 self-score rubric</strong> (Red/Yellow/Green/Blue)
              measures SME walk-through confidence per requirement. Feeds the composite pWin
              as a 5th signal once populated.
            </span>
          </p>
          {isClickable && (
            <button
              type="button"
              onClick={onNavigate}
              data-testid={`${dataTestId}-empty-cta`}
              className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-800 hover:underline"
            >
              Extract requirements on Intake <ExternalLink className="w-3 h-3" />
            </button>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function SelfScoreHeatmap({ summary, onNavigate, dataTestId = 'self-score-heatmap' }) {
  // v3.11.5 — render empty state instead of null
  if (!summary || summary.total_requirements === 0) {
    return <EmptyState onNavigate={onNavigate} dataTestId={dataTestId} />;
  }

  const total = summary.total_requirements;
  const dist = summary.distribution || {};
  const segments = ['red', 'yellow', 'green', 'blue', 'unscored'].map((key) => ({
    key,
    count: dist[key] || 0,
    pct: total > 0 ? ((dist[key] || 0) / total) * 100 : 0,
    color: SEGMENT_COLORS[key],
    label: SEGMENT_LABELS[key],
  }));

  const isClickable = typeof onNavigate === 'function';
  const Wrapper = isClickable ? 'button' : 'div';

  return (
    <TooltipProvider delayDuration={250}>
      <Wrapper
        type={isClickable ? 'button' : undefined}
        onClick={onNavigate}
        data-testid={dataTestId}
        className={cn(
          'w-full text-left rounded-md p-2 -mx-2 transition-colors group',
          isClickable && 'hover:bg-slate-100 cursor-pointer'
        )}
        title={isClickable ? 'Jump to Requirements on Opportunity Intake' : undefined}
      >
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-slate-600 text-sm flex items-center gap-1">
            Self-Score Coverage
            {isClickable && (
              <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </span>
          <span className="text-xs font-semibold text-slate-700">
            {summary.scored_count}/{total}
            <span className="text-slate-400 font-normal"> scored</span>
          </span>
        </div>
        {/* Stacked horizontal bar */}
        <div className="flex h-2 rounded overflow-hidden ring-1 ring-slate-200" data-testid={`${dataTestId}-bar`}>
          {segments.map((seg) =>
            seg.pct > 0 ? (
              <Tooltip key={seg.key}>
                <TooltipTrigger asChild>
                  <div
                    className={cn(seg.color, 'transition-opacity hover:opacity-80')}
                    style={{ width: `${seg.pct}%` }}
                    data-testid={`${dataTestId}-seg-${seg.key}`}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  <p className="font-semibold">{seg.label}</p>
                  <p className="opacity-90">{seg.count} of {total} ({Math.round(seg.pct)}%)</p>
                </TooltipContent>
              </Tooltip>
            ) : null
          )}
        </div>
        {/* Inline summary row */}
        <div className="flex items-center justify-between mt-1.5 text-[11px] text-slate-500">
          <span>
            <span className="text-emerald-600 font-medium">{summary.strong_fit_count}</span> strong (≥2)
          </span>
          {summary.average_score !== null && summary.average_score !== undefined && (
            <span>avg {summary.average_score}/3</span>
          )}
          {summary.unscored_count > 0 && (
            <span className="text-slate-400">{summary.unscored_count} un-scored</span>
          )}
        </div>
      </Wrapper>
    </TooltipProvider>
  );
}
